import { Avatar, Input, Table, TableColumnsType, Tabs, DatePicker } from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import { sortIcon } from "components/Table";
import TripAction from "./components/Action";
import useTrips from "hooks/trips/useTrip";
import {
  IPaymentDetail,
  ITripBooking,
  ITripDetailResponse,
  ITripDriver,
  ITripPassenger,
  ITripsModel,
  IAccount,
} from "models/trips";
import moment from "moment";
import Status from "./components/Status";
import { TripAlertContextProvider } from "./components/AlertContext";

export default function Trip() {
  const {
    data: { data, loading, page, per_page, search, total },
    statusFilter,
    dateFilter,
    handleSearch,
    handlePageChange,
    handleStatusChange,
    handleDateChange,
  } = useTrips();

  const columns: TableColumnsType<ITripsModel> = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => {
        return (page - 1) * per_page + index + 1;
      },
    },
    {
      title: "Driver",
      dataIndex: "driver",
      // sorter: (a, b) => {
      //   const aDriver = a.driver ? a.driver.first_name : "";
      //   const bDriver = b.driver ? b.driver.last_name : "";
      //   return aDriver.localeCompare(bDriver);
      // },
      render: (driver: ITripDriver) => (
        <div className="flex items-center gap-y-1 gap-x-1">
          <div className="w-full max-w-10 h-10">
            <Avatar
              shape="circle"
              size={40}
              src={driver?.image}
              icon={<UserOutlined />}
              className="max-w-10"
            />
          </div>
          <span className="whitespace-pre-line">
            {driver ? `${driver.first_name} ${driver.last_name}` : "No driver"}
          </span>
        </div>
      ),
      sortIcon,
    },
    {
      title: "Passenger",
      dataIndex: "passenger",
      // sorter: (a, b) => {
      //   const aPassenger = a.passenger ? a.passenger.first_name : "";
      //   const bPassenger = b.passenger ? b.passenger.first_name : "";
      //   return aPassenger.localeCompare(bPassenger);
      // },
      render: (passenger: ITripPassenger) => (
        <div className="flex items-center gap-y-1 gap-x-1">
          <div className="w-full max-w-10 h-10">
            <Avatar shape="circle" size={40} src={passenger?.image} icon={<UserOutlined />} />
          </div>
          <span className="whitespace-pre-line">
            {passenger ? `${passenger.first_name} ${passenger.last_name}` : "No passenger"}
          </span>
        </div>
      ),
      sortIcon,
    },
    {
      title: "Pickup Address",
      dataIndex: "pickup_address",
    },
    {
      title: "Drop off Address",
      dataIndex: "drop_off_address",
    },
    {
      title: "Started Date",
      dataIndex: "started_at",
      render: (started_at) => (
        <span className="text-nowrap">{started_at ? moment(started_at).format("lll") : "N/A"}</span>
      ),
      sortIcon,
    },
    {
      title: "Ended Date",
      dataIndex: "ended_at",
      render: (endedAt, record) => {
        if (!endedAt && record.status === "ongoing") {
          return <span className="text-blue-600 font-medium italic">Ongoing</span>;
        }
        if (!endedAt) {
          return <span className="text-gray-400 italic">Not ended</span>;
        }
        return <span className="text-nowrap">{moment(endedAt).format("lll")}</span>;
      },
      sortIcon,
    },
    {
      title: "Distance(km)",
      dataIndex: "distance",
      render: (value: number) => <p>{(value || 0).toFixed(2)}km</p>,
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (value: number) => <p>{(value || 0).toFixed(2)}</p>,
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record: ITripsModel) => {
        return <p>{duration(record)}</p>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: Status,
    },
    {
      title: "Finished By",
      dataIndex: "admin_compete",
      render: (admin: IAccount) => (
        admin ? (
          <div className="flex items-center gap-x-1">
             <Avatar size={24} src={admin.image} icon={<UserOutlined />} />
             <span className="text-xs">{`${admin.first_name} ${admin.last_name}`}</span>
          </div>
        ) : <span className="text-gray-400">-</span>
      ),
    },
    {
      title: "Admin Managed",
      dataIndex: "status_changed_by_admin",
      render: (admin: IAccount) => (
        admin ? (
          <div className="flex items-center gap-x-1">
             <Avatar size={24} src={admin.image} icon={<UserOutlined />} />
             <span className="text-xs">{`${admin.first_name} ${admin.last_name}`}</span>
          </div>
        ) : <span className="text-gray-400">-</span>
      ),
    },
    {
      title: "Action",
      render: (_, record) => <TripAction trip={record} />,
    },
  ];

  return (
    <TripAlertContextProvider>
      <div className="w-full">
        <Tabs
          activeKey={statusFilter}
          onChange={handleStatusChange}
          className="px-6"
          items={[
            { key: "all", label: "All" },
            { key: "pending", label: "Pending" },
            { key: "ongoing", label: "Ongoing" },
            { key: "completed", label: "Completed" },
            { key: "cancelled", label: "Cancelled" },
          ]}
        />
        <div className="w-full px-6 flex flex-wrap-reverse gap-4">
          <Input
            className="max-w-72 w-full h-[40px] px-4 py-2 text-base border border-[rgb(0,0,0,0.1)] rounded-3xl"
            prefix={<SearchOutlined className="mr-2" />}
            placeholder="Search..."
            onChange={handleSearch}
            value={search}
          />
          <DatePicker
            className="h-[40px] rounded-3xl"
            format="YYYY-MM-DD"
            placeholder="All Time"
            allowClear
            value={dateFilter && dateFilter !== "all" ? moment(dateFilter, "YYYY-MM-DD") as any : null}
            onChange={(date, dateString) => handleDateChange(typeof dateString === 'string' ? dateString : (Array.isArray(dateString) ? dateString[0] : "all"))}
          />
        </div>
        <div className="w-full mt-8 px-4">
          <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            onChange={handlePageChange}
            pagination={{
              total,
              pageSize: per_page,
              current: page,
              nextIcon: (
                <span className="flex items-center justify-center w-8 h-8 text-2xl">
                  <NextIcon />
                </span>
              ),
              prevIcon: (
                <span className="flex items-center justify-center w-8 h-8 text-2xl">
                  <PreviousIcon />
                </span>
              ),
            }}
          />
        </div>
      </div>
    </TripAlertContextProvider>
  );
}

const duration = (record: ITripsModel) => {
  const startedAt = record?.started_at;
  const endedAt = (record as any)?.ended_at; // ended_at might not be in type definition but exists in API response

  if (startedAt && endedAt) {
    const start = moment(startedAt);
    const end = moment(endedAt);
    const diffSeconds = end.diff(start, "seconds");

    if (diffSeconds <= 0) {
      return "0 seconds";
    }

    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? "s" : ""}`;
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      const seconds = diffSeconds % 60;
      if (seconds > 0) {
        return `${minutes} min ${seconds} sec`;
      }
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    } else {
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      if (minutes > 0) {
        return `${hours} hr ${minutes} min`;
      }
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    }
  }

  if (startedAt && !endedAt) {
    return "Ongoing";
  }

  return "N/A";
};
